import { FC, useId, useRef } from 'react'

import { Carat, Trash } from '@syconium/little-miss-figgy'

import {
  OptionRowButton,
  OptionRowEdit,
  OptionRowLabel,
  OptionRowPrice,
  OptionRowReason,
  OptionRowShell,
  OptionRowStatic,
  OptionRowSubLabel,
  OptionRowText,
  OptionRowTrailing,
  OptionRowTrash,
} from './styles'

export type PanelOptionRowProps = {
  /** The row's heading, e.g. "TEXT". */
  label: string
  subLabel?: string
  priceLabel?: string
  /**
   * Why the row cannot be opened. Setting it greys the row and takes its
   * controls away, so it outranks `onOpen` and `onRemove`.
   */
  unavailableReason?: string
  editLabel?: string
  onOpen?: () => void
  openLabel?: string
  onRemove?: () => void
  removeLabel?: string
}

/**
 * One row of the options panel — a type of embroidery, or a garment choice like color or size.
 */
export const PanelOptionRow: FC<PanelOptionRowProps> = ({
  label,
  subLabel,
  priceLabel,
  unavailableReason,
  editLabel,
  onOpen,
  openLabel,
  onRemove,
  removeLabel,
}) => {
  const subLabelId = useId()
  const priceId = useId()
  const openButtonRef = useRef<HTMLButtonElement>(null)

  if (unavailableReason !== undefined) {
    return (
      <OptionRowShell>
        <OptionRowStatic>
          <OptionRowText>
            <OptionRowLabel $disabled>{label}</OptionRowLabel>
            <OptionRowReason>{unavailableReason}</OptionRowReason>
          </OptionRowText>
        </OptionRowStatic>
      </OptionRowShell>
    )
  }

  const isFilled = onRemove !== undefined
  // aria-label replaces the visible text, so the sub-label and price are read as the description.
  const describedBy =
    openLabel !== undefined &&
    [subLabel !== undefined && subLabelId, priceLabel !== undefined && priceId]
      .filter(Boolean)
      .join(' ')

  return (
    <OptionRowShell>
      <OptionRowButton
        ref={openButtonRef}
        type='button'
        onClick={onOpen}
        aria-label={openLabel}
        aria-describedby={describedBy || undefined}
      >
        <OptionRowText>
          <OptionRowLabel>{label}</OptionRowLabel>
          {subLabel !== undefined && (
            <OptionRowSubLabel id={subLabelId}>{subLabel}</OptionRowSubLabel>
          )}
        </OptionRowText>
        <OptionRowTrailing>
          {isFilled && editLabel !== undefined && <OptionRowEdit>{editLabel}</OptionRowEdit>}
          {priceLabel !== undefined && <OptionRowPrice id={priceId}>{priceLabel}</OptionRowPrice>}
          {!isFilled && <Carat height='12px' width='12px' strokeWidth='1.1' />}
        </OptionRowTrailing>
      </OptionRowButton>

      {onRemove !== undefined && (
        <OptionRowTrash
          type='button'
          onClick={() => {
            onRemove()
            // The trash button unmounts with the removal; keep focus on the row.
            openButtonRef.current?.focus()
          }}
          aria-label={removeLabel}
        >
          <Trash height='16px' width='16px' />
        </OptionRowTrash>
      )}
    </OptionRowShell>
  )
}
